import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { Search, Filter, Calendar, Clock, User, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useUserPortalTickets } from '@/hooks/useUserPortalTickets';
import { TicketDetailsModal } from '@/components/UserPortal/TicketDetailsModal';

interface Ticket {
  id: string;
  numero: string;
  titulo: string;
  descricao?: string;
  status: string;
  urgencia: string;
  prioridade: string;
  criado_em: string;
  atualizado_em: string;
  categoria_id?: string;
  atendente_id?: string;
  categoria_nome?: string | null;
  atendente_nome?: string | null;
}

export const UserTicketsList: React.FC = () => {
  const { tickets, isLoading } = useUserPortalTickets();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewTicket = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTicket(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aberta':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'em_andamento':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'pendente':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'resolvida':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'fechada':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getUrgencyColor = (urgencia: string) => {
    switch (urgencia) {
      case 'baixa':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'media':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'alta':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'critica':
        return 'bg-red-200 text-red-900 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'aberta': return 'Aberta';
      case 'em_andamento': return 'Em Andamento';
      case 'pendente': return 'Pendente';
      case 'resolvida': return 'Resolvida';
      case 'fechada': return 'Fechada';
      default: return status;
    }
  };

  const getUrgencyLabel = (urgencia: string) => {
    switch (urgencia) {
      case 'baixa': return 'Baixa';
      case 'media': return 'Média';
      case 'alta': return 'Alta';
      case 'critica': return 'Crítica';
      default: return urgencia;
    }
  };

  // Filtrar tickets
  const filteredTickets = (tickets || []).filter((ticket: Ticket) => {
    const matchesSearch = ticket.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (ticket.descricao && ticket.descricao.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    const matchesUrgency = urgencyFilter === 'all' || ticket.urgencia === urgencyFilter;
    
    return matchesSearch && matchesStatus && matchesUrgency;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageTickets = filteredTickets.slice(startIndex, endIndex);

  // Reset pagination when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, urgencyFilter]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Filter className="h-5 w-5 text-blue-600" />
              Filtros e Busca
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-10 skeleton rounded-lg"></div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle>Carregando tickets...</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border border-slate-200 rounded-xl p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="h-8 w-24 skeleton rounded-md"></div>
                  <div className="h-6 w-20 skeleton rounded-full"></div>
                  <div className="h-6 w-16 skeleton rounded-full"></div>
                </div>
                <div className="h-6 skeleton rounded w-3/4"></div>
                <div className="h-16 skeleton rounded"></div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="h-4 skeleton rounded"></div>
                  <div className="h-4 skeleton rounded"></div>
                  <div className="h-4 skeleton rounded"></div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtros e Busca */}
      <Card className="border-slate-200 card-lift">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-display">
            <Filter className="h-5 w-5 text-blue-600" />
            Filtros e Busca
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <Input
                placeholder="Buscar por título, número..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                <SelectItem value="aberta">Aberta</SelectItem>
                <SelectItem value="em_andamento">Em Andamento</SelectItem>
                <SelectItem value="pendente">Pendente</SelectItem>
                <SelectItem value="resolvida">Resolvida</SelectItem>
                <SelectItem value="fechada">Fechada</SelectItem>
              </SelectContent>
            </Select>

            <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Urgência" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as Urgências</SelectItem>
                <SelectItem value="baixa">Baixa</SelectItem>
                <SelectItem value="media">Média</SelectItem>
                <SelectItem value="alta">Alta</SelectItem>
                <SelectItem value="critica">Crítica</SelectItem>
              </SelectContent>
            </Select>

            <Button 
              variant="outline" 
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setUrgencyFilter('all');
              }}
              className="hover:bg-slate-50 hover:border-slate-400 transition-all duration-300"
            >
              Limpar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Tickets */}
      <Card className="border-slate-200 card-lift">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2 font-display">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Meus Tickets ({filteredTickets.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTickets.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquare className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-display font-semibold text-slate-900 mb-2">Nenhum ticket encontrado</h3>
              <p className="text-slate-600">
                {searchTerm || statusFilter !== 'all' || urgencyFilter !== 'all' 
                  ? 'Tente ajustar os filtros de busca.'
                  : 'Você ainda não possui tickets criados.'}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {currentPageTickets.map((ticket: Ticket, index: number) => (
                <div
                  key={ticket.id}
                  className="border-2 border-slate-200 rounded-2xl p-6 hover:shadow-xl hover:border-blue-300 transition-all duration-300 card-lift group animate-fade-in"
                  style={{ '--tw-animation-delay': `${index * 0.05}s` } as React.CSSProperties}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3 flex-wrap">
                       <span 
                        onClick={() => handleViewTicket(ticket)}
                        className="font-display font-bold text-blue-600 bg-blue-50 px-4 py-2 rounded-xl cursor-pointer hover:bg-blue-100 hover:shadow-md transition-all duration-300 hover:scale-105"
                      >
                        {ticket.numero}
                      </span>
                      <Badge className={`${getStatusColor(ticket.status)} px-3 py-1 font-semibold`}>
                        {getStatusLabel(ticket.status)}
                      </Badge>
                      <Badge className={`${getUrgencyColor(ticket.urgencia)} px-3 py-1 font-semibold`}>
                        {getUrgencyLabel(ticket.urgencia)}
                      </Badge>
                    </div>
                  </div>

                  <h3 className="font-display font-bold text-slate-900 mb-3 text-xl group-hover:text-blue-600 transition-colors">
                    {ticket.titulo}
                  </h3>

                  {ticket.descricao && (
                    <p className="text-slate-600 mb-4 line-clamp-2 leading-relaxed">
                      {ticket.descricao}
                    </p>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-600 bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-600" />
                      <span className="font-medium">Criado: {format(new Date(ticket.criado_em), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-amber-600" />
                      <span className="font-medium">Atualizado: {format(new Date(ticket.atualizado_em), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</span>
                    </div>

                    {ticket.atendente_nome && (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-emerald-600" />
                        <span className="font-medium">Atendente: {ticket.atendente_nome}</span>
                      </div>
                    )}
                  </div>

                  {ticket.categoria_nome && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <span className="text-sm text-slate-600">
                        Categoria: <span className="font-semibold text-slate-900">{ticket.categoria_nome}</span>
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {filteredTickets.length > itemsPerPage && (
        <Card className="border-slate-200">
          <CardContent className="flex justify-center py-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show first page, last page, current page, and pages around current
                  const shouldShow = 
                    page === 1 || 
                    page === totalPages || 
                    (page >= currentPage - 1 && page <= currentPage + 1);
                  
                  if (!shouldShow) {
                    // Show ellipsis for gaps
                    if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <PaginationItem key={page}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      );
                    }
                    return null;
                  }
                  
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => setCurrentPage(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                
                <PaginationItem>
                  <PaginationNext 
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </CardContent>
        </Card>
      )}

      {/* Modal de Detalhes do Ticket */}
      <TicketDetailsModal
        ticket={selectedTicket}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};
